import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { cn } from "@/lib/utils.js";

/**
 * CustomDateRangePicker component
 * Displays two date inputs with a separator and a clear button
 *
 * TIMEZONE NOTE:
 * - HTML5 date inputs (<input type="date">) work in the user's local timezone
 * - When a user selects a date, it's interpreted as that date in their local timezone
 * - The selected dates (YYYY-MM-DD format) are then converted to UTC when sent to the API
 * - This ensures users see dates in their familiar timezone while API receives consistent UTC dates
 *
 * @param {Object} props - Component props
 * @param {string} props.startDate - Start date value (YYYY-MM-DD format, interpreted in local timezone)
 * @param {string} props.endDate - End date value (YYYY-MM-DD format, interpreted in local timezone)
 * @param {Function} props.onApply - Callback when apply button is clicked with (startDate, endDate)
 * @param {Function} props.onClear - Callback when clear button is clicked
 * @param {boolean} [props.disabled] - Whether the inputs are disabled
 * @returns {JSX.Element} Custom date range picker
 */
export default function CustomDateRangePicker({
  startDate,
  endDate,
  onApply,
  onClear,
  disabled = false,
}) {
  const { theme } = useTheme();
  const [inputStartDate, setInputStartDate] = useState(startDate);
  const [inputEndDate, setInputEndDate] = useState(endDate);
  const [inputDateError, setInputDateError] = useState("");
  const dateInputClassName = cn(
    "flex-1 min-w-0",
    theme === "dark" ? "scheme-dark" : "scheme-light"
  );

  const validateDate = (value, type) => {
    if (!value) {
      return type + " Date is Required.";
    }
    if (
      (type === "End"
        && new Date(inputStartDate).getTime() > new Date(value).getTime())
      || (type === "Start"
        && inputEndDate
        && new Date(value).getTime() > new Date(inputEndDate).getTime())
    ) {
      return "End Date must be after Start Date.";
    }
    return "";
  };

  // Sync local state with props when they change externally
  useEffect(() => {
    setInputStartDate(startDate);
  }, [startDate]);

  useEffect(() => {
    setInputEndDate(endDate);
  }, [endDate]);

  return (
    <div className='flex items-center gap-2 w-full'>
      <div className='flex flex-col flex-1 min-w-0'>
        <div className='flex items-center gap-2 w-full'>
          <Input
            type='date'
            value={inputStartDate}
            onChange={(e) => {
              const { value } = e.target;
              setInputStartDate(value);
              setInputDateError(validateDate(value, "Start"));
            }}
            className={dateInputClassName}
            style={{ colorScheme: theme }}
            disabled={disabled}
          />
          <span className='text-muted-foreground flex-shrink-0'>to</span>
          <Input
            type='date'
            value={inputEndDate}
            onChange={(e) => {
              const { value } = e.target;
              setInputEndDate(value);
              setInputDateError(validateDate(value, "End"));
            }}
            className={dateInputClassName}
            style={{ colorScheme: theme }}
            disabled={disabled}
          />
        </div>
        {inputDateError && (
          <span className='text-red-600 text-xs self-center mt-1'>
            {inputDateError}
          </span>
        )}
      </div>
      <button
        onClick={() => onApply(inputStartDate, inputEndDate)}
        className='p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0'
        title='Apply custom date range'
        disabled={
          inputDateError || disabled || !inputStartDate || !inputEndDate
        }>
        <Check className='h-4 w-4' />
      </button>
      <button
        onClick={onClear}
        className='p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0'
        title='Remove custom date range'
        disabled={disabled}>
        <X className='h-4 w-4' />
      </button>
    </div>
  );
}
